import { useEffect, useLayoutEffect, useState, useSyncExternalStore } from "react";
import type { Character } from "@/types/screenplay";
import type { Json } from "@/integrations/supabase/types";
import { supabase } from "@/integrations/supabase/client";
import { ScreenplaySaveQueue } from "@/utils/screenplaySaveQueue";

export function useScreenplaySave(
  userId: string | undefined,
  screenplayId: string | null,
  characters: Character[],
  assignments: Record<string, string>,
) {
  const key = JSON.stringify([userId, screenplayId]);
  const snapshot = JSON.stringify({
    character_assignments: assignments,
    parsed_characters: characters,
    parsed_sequence: characters.find((character) => character.name === "SCREENPLAY_SEQUENCE")?.sequence ?? [],
  });
  const createSession = () => ({
    key,
    queue: new ScreenplaySaveQueue(snapshot, async (value) => {
      if (!userId || !screenplayId) throw new Error("No open screenplay");
      const { data, error } = await supabase.from("screenplays")
        .update(JSON.parse(value) as {
          character_assignments: Json; parsed_characters: Json; parsed_sequence: Json;
        })
        .eq("id", screenplayId).eq("user_id", userId)
        .select("id").single();
      // RLS, a deleted row, and an expired session must never look like success.
      if (error || data?.id !== screenplayId) throw new Error("Save not acknowledged");
    }),
  });
  const [session, setSession] = useState(createSession);
  // A new document/user gets a fresh baseline, without autosaving restored data.
  if (session.key !== key) setSession(createSession());
  const { queue } = session;
  const state = useSyncExternalStore(queue.subscribe, queue.getSnapshot);

  useLayoutEffect(() => {
    if (session.key === key && userId && screenplayId) queue.update(snapshot);
    return queue.cancelScheduled;
  }, [key, queue, screenplayId, session.key, snapshot, userId]);

  useEffect(() => {
    if (!state.hasUnsavedChanges) return;
    const warnBeforeLeaving = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeLeaving);
    return () => window.removeEventListener("beforeunload", warnBeforeLeaving);
  }, [state.hasUnsavedChanges]);

  return { state, save: queue.flush, acceptSaved: (savedCharacters:Character[],savedAssignments:Record<string,string>) =>
    queue.adoptAcknowledged(snapshot,JSON.stringify({character_assignments:savedAssignments,parsed_characters:savedCharacters,
      parsed_sequence:savedCharacters.find(c=>c.name==='SCREENPLAY_SEQUENCE')?.sequence??[]})) };
}
